/**
 * <biosphere-quantity> — enhancement for the quantity input rendered by
 * snippets/biosphere-quantity.liquid. Behaviour: reference/specs/quantity-input.md.
 *
 * The rule constraints live on the native input; the effective maximum — what may
 * still be added — is rule max minus what the cart already holds, floored at min.
 * Volume-pricing tiers arrive server-formatted; this file only swaps strings.
 */
class BiosphereQuantity extends HTMLElement {
  #input;
  #minus;
  #plus;
  #perItem;
  #abort;

  connectedCallback() {
    this.#input = this.querySelector(".biosphere-quantity__input");
    this.#perItem = this.querySelector(".biosphere-quantity__per-item");
    const steppers = this.querySelectorAll(".biosphere-quantity__step");
    this.#minus = [...steppers].find((b) => b.dataset.step === "-1");
    this.#plus = [...steppers].find((b) => b.dataset.step === "1");

    // A disabled input means an unavailable variant — the native baseline
    // stands and the steppers stay hidden.
    if (!this.#input || !this.#minus || !this.#plus || this.#input.disabled) {
      return;
    }

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.#minus.addEventListener("click", () => this.#stepBy(-1), { signal });
    this.#plus.addEventListener("click", () => this.#stepBy(1), { signal });
    this.#input.addEventListener("blur", () => this.#commit(), { signal });
    this.#input.addEventListener("focus", () => this.#input.select(), {
      signal,
    });

    // Someone else moved the input. This component owns it, so a write from outside has to
    // become the same event as a press of its own buttons — otherwise a second stepper
    // silently desynchronises the tier ladder, the prices and the disabled states.
    //
    // Only while the field is not focused: a change event from a buyer typing belongs to
    // blur, which validates the increment before announcing anything.
    this.#input.addEventListener(
      "change",
      () => {
        if (this.#input === document.activeElement) return;
        this.#input.value = String(this.#clamp(this.value));
        this.#changed();
      },
      { signal },
    );

    this.#minus.hidden = false;
    this.#plus.hidden = false;
    this.#refresh();
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  get value() {
    return Number.parseInt(this.#input?.value ?? "", 10) || 0;
  }

  /** Whether adding the current value would exceed the rule maximum. */
  canAdd() {
    const { max, cart } = this.#values();
    if (max === null) return { canAdd: true, max: null };
    return { canAdd: cart + this.value <= max, max };
  }

  /** Replace constraints (variant change): snap down to the new increment,
      then clamp. The buyer's value survives when still legal. */
  setConstraints(min, max, step) {
    if (!this.#input) return;
    this.#input.min = min ?? "1";
    if (max) {
      this.#input.max = max;
    } else {
      this.#input.removeAttribute("max");
    }
    this.#input.step = step ?? "1";

    const values = this.#values();
    let value = this.value;
    if ((value - values.min) % values.step !== 0) {
      value =
        values.min +
        Math.floor((value - values.min) / values.step) * values.step;
    }
    this.#input.value = String(this.#clamp(value));
    this.#refresh();
  }

  /** The cart changed elsewhere: recompute the effective max and re-clamp. */
  setCartQuantity(quantity) {
    if (!this.#input) return;
    this.#input.dataset.cartQuantity = String(quantity);
    this.#input.value = String(this.#clamp(this.value));
    this.#refresh();
  }

  #values() {
    const input = this.#input;
    return {
      min: Number.parseInt(input.min, 10) || 1,
      max: input.max === "" ? null : Number.parseInt(input.max, 10),
      step: Number.parseInt(input.step, 10) || 1,
      cart: Number.parseInt(input.dataset.cartQuantity ?? "", 10) || 0,
    };
  }

  #effectiveMax() {
    const { min, max, cart } = this.#values();
    if (max === null) return null;
    // Cart-line mode: the input is the total that should be in the cart,
    // so the rule max applies absolutely (spec: cart-line semantics).
    if (this.hasAttribute("data-cart-mode")) return max;
    return Math.max(max - cart, min);
  }

  #clamp(value) {
    const { min } = this.#values();
    const max = this.#effectiveMax();
    return Math.min(max ?? Infinity, Math.max(min, value));
  }

  #stepBy(direction) {
    const { step } = this.#values();
    this.#input.value = String(this.#clamp(this.value + step * direction));
    this.#changed();
  }

  #commit() {
    const { min, step } = this.#values();
    const clamped = this.#clamp(this.value);
    this.#input.value = String(clamped);
    // Off-increment values surface the platform's own validity message
    // instead of being silently corrected.
    if ((clamped - min) % step !== 0) {
      this.#input.reportValidity();
      return;
    }
    this.#changed();
  }

  #changed() {
    this.#refresh();
    this.dispatchEvent(
      new CustomEvent("biosphere:quantity:change", {
        bubbles: true,
        detail: {
          value: this.value,
          productId: this.dataset.productId,
          line: Number.parseInt(this.dataset.line ?? "", 10) || undefined,
        },
      }),
    );
  }

  #refresh() {
    const { min, cart } = this.#values();
    const max = this.#effectiveMax();
    this.#minus.disabled = this.value <= min;
    this.#plus.disabled = max !== null && this.value >= max;

    // Volume pricing: pick the highest tier whose minimum is covered by
    // cart + current value; tiers arrive ascending and pre-formatted.
    if (this.#perItem) {
      const template = this.#perItem.dataset.template ?? "";
      let price = this.#perItem.dataset.basePrice ?? "";
      let breaks = [];
      try {
        breaks = JSON.parse(this.#perItem.dataset.priceBreaks ?? "[]");
      } catch {
        breaks = [];
      }
      for (const tier of breaks) {
        if (cart + this.value >= tier.quantity) price = tier.price;
      }
      this.#perItem.textContent = template.replace("[price]", price);
    }
  }
}

if (!customElements.get("biosphere-quantity")) {
  customElements.define("biosphere-quantity", BiosphereQuantity);
}
